import Decimal from 'decimal.js'

import { buildScope, resolveHedgeInstrument } from '@/features/hedge/lib/build-hedge-params'
import {
  isPositiveWholeNumber,
  isSignedDecimal,
  validateHedgeScope,
} from '@/features/hedge/lib/hedge-bands'
import { HedgeFormModel, HedgeValidationError } from '@/features/hedge/types'
import { SingleHedgeAlgoParams, SingleHedgeExecution } from '@/types/generated/algo'
import { Position } from '@/types/generated/common/trading'
import { Exchange, Instrument, TradeVenue } from '@/types/generated/common/types'

/** How a single hedge's order is worked — the part of it the hedge form model doesn't hold. */
export interface SingleHedgeExecutionForm {
  // UNSPECIFIED = the server picks MARKET, or TWAP for a large leg.
  execution: SingleHedgeExecution
  // Only sent with TWAP execution; blank = the server's 60s.
  twapSeconds: string
}

/** The hedge scope checks, plus the target and execution a single hedge reads instead of bands. */
export function validateSingleHedge(
  form: HedgeFormModel,
  exec: SingleHedgeExecutionForm,
  positions: Position[],
  instrumentMap: Map<string, Instrument>,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
): HedgeValidationError[] {
  const errors = validateHedgeScope(form, positions, instrumentMap, multiExchangeInstrumentMap)
  if (!isSignedDecimal(form.acceptable.target)) {
    errors.push({ field: 'acceptable.target', message: 'Enter a signed decimal' })
  }
  if (
    exec.execution === SingleHedgeExecution.SINGLE_HEDGE_EXECUTION_TWAP &&
    exec.twapSeconds.trim() !== '' &&
    !isPositiveWholeNumber(exec.twapSeconds)
  ) {
    errors.push({
      field: 'twapSeconds',
      message: 'TWAP duration (seconds) must be a whole number greater than 0',
    })
  }
  return errors
}

/**
 * A single hedge to the acceptable band's target. Scope and instrument resolve as for the
 * continuous hedge; `preview` stays empty because the server sizes every leg itself.
 */
export function buildSingleHedgeParams(
  form: HedgeFormModel,
  exec: SingleHedgeExecutionForm,
  accountId: string,
  positions: Position[],
  instrumentMap: Map<string, Instrument>,
  multiExchangeInstrumentMap: Map<Exchange, Map<string, Instrument>>
): SingleHedgeAlgoParams {
  const target = form.acceptable.target.trim()
  const params: SingleHedgeAlgoParams = {
    accountId,
    exchange: Exchange.EXCHANGE_UNSPECIFIED,
    tradeVenue: TradeVenue.TRADE_VENUE_UNSPECIFIED,
    scope: buildScope(form),
    denomination: form.denomination,
    execution: exec.execution,
    preview: [],
  }
  // hedge_instrument is proto3 `optional`: an empty string would still be sent as present, so
  // a multi-underlying scope (no single instrument) leaves it off for the server's default perp.
  const hedgeInstrument = resolveHedgeInstrument(
    form,
    positions,
    instrumentMap,
    multiExchangeInstrumentMap
  )
  if (hedgeInstrument) params.hedgeInstrument = hedgeInstrument
  // Absent target = flat, so a zero target is left off the wire.
  if (isSignedDecimal(target) && !new Decimal(target).isZero()) params.target = target
  const twapSeconds = exec.twapSeconds.trim()
  if (exec.execution === SingleHedgeExecution.SINGLE_HEDGE_EXECUTION_TWAP && twapSeconds) {
    params.twapDurationSeconds = twapSeconds
  }
  return params
}
